import { FormHandles } from '@unform/core';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { ModalHeader } from '../../components/ModalHeader';
import { Icon } from '../../components/Icon';
import { Select, optionProps } from '../../components/Select';
import {
  EMPLOYEE_PRESET,
  GYM_MANAGER_PRESET,
} from '../../utils/permissionPresets';
import { IGym } from '../../domains/Gym';
import { colors } from '../../styles/tokens';
import { useModal } from '../../hooks/modal';
import { useToast } from '../../hooks/toast';
import { AdminUsersService } from '../../services/AdminUsersService';
import { GymsService } from '../../services/GymsService';

import {
  Cancel,
  Container,
  Content,
  FixedEmailInfo,
  FooterRow,
  FormContainer,
  GymResultIcon,
  GymResultName,
  GymResultRow,
  GymResultSubtitle,
  GymResultsList,
  GymSearchIconWrapper,
  GymSearchInput,
  GymSearchWrapper,
  Input,
  RoleFieldLabel,
  SelectedGymInfo,
  SelectedGymRow,
  SwapGymButton,
  Submit,
} from './styles';

type RoleOption = 'aluno' | 'funcionario' | 'gestor';

const ROLE_OPTIONS: optionProps[] = [
  { value: 'aluno', label: 'Nenhuma (aluno)' },
  { value: 'funcionario', label: 'Funcionário' },
  { value: 'gestor', label: 'Gestor da academia' },
];

const ROLE_PRESET_CODES: Record<RoleOption, string[]> = {
  aluno: [],
  funcionario: EMPLOYEE_PRESET,
  gestor: GYM_MANAGER_PRESET,
};

interface IAssociateGymUserProps {
  fixedEmail?: string;
  userName?: string;
  onSuccess?: () => void;
}

const AssociateGymUser: React.FC<IAssociateGymUserProps> = ({
  fixedEmail,
  userName,
  onSuccess,
}) => {
  const formRef = useRef<FormHandles>(null);
  const { addToast } = useToast();
  const { removeModal } = useModal();

  const adminUsersService = new AdminUsersService();
  const gymsService = new GymsService();

  const [submitting, setSubmitting] = useState(false);

  const [gymSearchTerm, setGymSearchTerm] = useState('');
  const [gymResults, setGymResults] = useState<IGym[]>([]);
  const [selectedGym, setSelectedGym] = useState<IGym>();
  const [role, setRole] = useState<RoleOption>('aluno');

  useEffect(() => {
    const timer = setTimeout(() => {
      gymsService
        .getGyms({ search: gymSearchTerm, limit: 8 })
        .then(response => setGymResults(response.data.data))
        .catch(() => undefined);
    }, 300);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gymSearchTerm]);

  const handleSelectGym = useCallback((gym: IGym) => {
    setSelectedGym(gym);
    setGymSearchTerm('');
    setGymResults([]);
  }, []);

  const handleSubmit = useCallback(
    async (data: Record<string, string>) => {
      const email = (fixedEmail || String(data.email || ''))
        .trim()
        .toLowerCase();

      if (!email) {
        addToast({ type: 'error', title: 'Informe o e-mail do usuário.' });
        return;
      }

      if (!selectedGym) {
        addToast({ type: 'error', title: 'Selecione uma academia.' });
        return;
      }

      try {
        setSubmitting(true);

        await adminUsersService.associateUser({
          email,
          gym_id: selectedGym.id,
          permission_codes: ROLE_PRESET_CODES[role],
        });

        addToast({
          type: 'success',
          title: 'Usuário associado com sucesso.',
        });

        onSuccess?.();
        removeModal();
      } catch (err: any) {
        if (err?.response?.status === 404) {
          addToast({
            type: 'error',
            title: 'Usuário não encontrado com esse e-mail.',
          });
          return;
        }

        if (err?.response?.status === 401 || err?.response?.status === 403) {
          addToast({
            type: 'error',
            title: 'Você não tem permissão para executar essa ação.',
          });
          return;
        }

        if (err?.response?.status === 400) {
          addToast({
            type: 'error',
            title: 'Esse usuário já está associado a essa academia.',
          });
          return;
        }

        addToast({
          type: 'error',
          title: 'Houve um erro. Tente novamente.',
        });
      } finally {
        setSubmitting(false);
      }
    },
    [
      addToast,
      fixedEmail,
      adminUsersService,
      onSuccess,
      removeModal,
      role,
      selectedGym,
    ],
  );

  return (
    <Container>
      <ModalHeader
        title="Vincular a uma academia"
        subtitle={
          fixedEmail
            ? 'Associar a uma nova academia'
            : 'Vincula um usuário que já existe a uma academia'
        }
      />

      <Content>
        <FormContainer ref={formRef} onSubmit={handleSubmit}>
          {fixedEmail ? (
            <FixedEmailInfo>
              Associando <strong>{userName || fixedEmail}</strong>
            </FixedEmailInfo>
          ) : (
            <Input label="E-mail do usuário" name="email" />
          )}

          {selectedGym ? (
            <SelectedGymRow>
              <GymResultIcon>
                <Icon name="check" size={16} color={colors.primary} />
              </GymResultIcon>
              <SelectedGymInfo>
                <GymResultName>{selectedGym.name}</GymResultName>
                {(selectedGym.address_city || selectedGym.address_uf) && (
                  <GymResultSubtitle>
                    {[selectedGym.address_city, selectedGym.address_uf]
                      .filter(Boolean)
                      .join(' · ')}
                  </GymResultSubtitle>
                )}
              </SelectedGymInfo>
              <SwapGymButton
                type="button"
                onClick={() => setSelectedGym(undefined)}
              >
                Trocar
              </SwapGymButton>
            </SelectedGymRow>
          ) : (
            <>
              <GymSearchWrapper>
                <GymSearchIconWrapper>
                  <Icon
                    name="search"
                    size={18}
                    color={colors.onSurfaceVariant}
                  />
                </GymSearchIconWrapper>
                <GymSearchInput
                  type="text"
                  placeholder="Buscar academia por nome ou cidade..."
                  value={gymSearchTerm}
                  onChange={e => setGymSearchTerm(e.target.value)}
                />
              </GymSearchWrapper>

              {!!gymResults.length && (
                <GymResultsList>
                  {gymResults.map(gym => (
                    <GymResultRow
                      key={gym.id}
                      type="button"
                      onClick={() => handleSelectGym(gym)}
                    >
                      <GymResultIcon>
                        <Icon name="add" size={16} color={colors.primary} />
                      </GymResultIcon>
                      <SelectedGymInfo>
                        <GymResultName>{gym.name}</GymResultName>
                        {(gym.address_city || gym.address_uf) && (
                          <GymResultSubtitle>
                            {[gym.address_city, gym.address_uf]
                              .filter(Boolean)
                              .join(' · ')}
                          </GymResultSubtitle>
                        )}
                      </SelectedGymInfo>
                      <Icon
                        name="chevron_right"
                        size={18}
                        color={colors.onSurfaceVariant}
                      />
                    </GymResultRow>
                  ))}
                </GymResultsList>
              )}
            </>
          )}

          <RoleFieldLabel>Cargo inicial</RoleFieldLabel>
          <Select
            name="role"
            options={ROLE_OPTIONS}
            defaultValue={ROLE_OPTIONS[0]}
            isSearchable={false}
            onChange={(option: unknown) =>
              setRole((option as optionProps).value as RoleOption)
            }
          />

          <FooterRow>
            <Cancel type="button" onClick={() => removeModal()}>
              Cancelar
            </Cancel>
            <Submit
              name="submit"
              value="Vincular"
              loading={submitting}
              disabled={submitting}
            />
          </FooterRow>
        </FormContainer>
      </Content>
    </Container>
  );
};

export { AssociateGymUser };
